import { Tooltip } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import type { NavigationLinkConfig } from "../types";

interface NavigationLinkProps {
    config: NavigationLinkConfig;
    /** Callback when link is clicked */
    onClick?: () => void;
    /** Variant for styling differences */
    variant?: "default" | "book";
    /** Whether the navigation is collapsed (icons only) */
    isCollapsed?: boolean;
}

/**
 * Reusable navigation link component with tooltip support
 * Shows only icon when collapsed, full content when expanded
 */
const NavigationLink = ({ config, onClick, variant = "default", isCollapsed = false }: NavigationLinkProps) => {
    const { t } = useTranslation();
    const { to, icon, label, disabled = false, customComponent, disabledTooltip } = config;

    const handleClick = (e: React.MouseEvent) => {
        if (disabled) {
            e.preventDefault();
            return;
        }
        onClick?.();
    };

    const linkClassName = classNames("navigation-link", {
        "navigation-link--book": variant === "book",
        "navigation-link--collapsed": isCollapsed,
        enabled: !disabled,
        disabled: disabled,
    });

    // Show tooltip with label when collapsed, or a specific tooltip when disabled
    const getTooltipTitle = () => {
        if (disabled) return disabledTooltip ?? t("NavigationBar.NoAccess");
        if (isCollapsed) return t(label);
        return "";
    };

    return (
        <Tooltip title={getTooltipTitle()} placement="right" arrow>
            <NavLink className={linkClassName} to={to} onClick={handleClick}>
                {customComponent ? (
                    customComponent
                ) : (
                    <>
                        <div className="navigation-link__icon">{icon}</div>
                        <span className="navigation-link__label">{t(label)}</span>
                    </>
                )}
            </NavLink>
        </Tooltip>
    );
};

export default NavigationLink;
